import { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { MessageCircle, Save, RotateCcw, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * Editor for the two WhatsApp templates (male / female) used when the admin
 * taps the "واتساب" button on an inactive reader card.
 */
const WhatsAppTemplatesSection = ({ isMaster = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [male, setMale] = useState('');
  const [female, setFemale] = useState('');
  const [defaults, setDefaults] = useState({ male: '', female: '' });
  const [variables, setVariables] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/whatsapp-templates');
        setMale(data.male || '');
        setFemale(data.female || '');
        setDefaults(data.defaults || {});
        setVariables(data.variables || []);
      } catch {
        toast.error('فشل تحميل قوالب واتساب');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    if (!male.trim() || !female.trim()) {
      toast.error('كلا القالبين مطلوبان');
      return;
    }
    setSaving(true);
    try {
      await api.put('/admin/whatsapp-templates', { male, female });
      toast.success('تم حفظ قوالب واتساب');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل حفظ القوالب');
    } finally {
      setSaving(false);
    }
  };

  const resetTo = (which) => {
    if (which === 'male') setMale(defaults.male || '');
    if (which === 'female') setFemale(defaults.female || '');
    toast.info('تمت الاستعادة (اضغط «حفظ» لتفعيلها)');
  };

  if (loading) {
    return (
      <Card className="p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </Card>
    );
  }

  return (
    <Card className="p-6 space-y-4 border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white" data-testid="whatsapp-templates-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-lg font-bold text-accent flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-emerald-600" />
          قوالب رسائل واتساب — الحسابات الخاملة
        </h3>
        {isMaster && (
          <Button
            onClick={save}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
            data-testid="save-whatsapp-templates-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            حفظ القوالب
          </Button>
        )}
      </div>

      <div className="flex items-start gap-2 text-xs bg-white/70 border border-emerald-200 rounded-lg p-3 text-emerald-900">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          هذه هي الرسائل التي تُفتح في واتساب حين ينقر الأدمن على زر «واتساب» في صفحة الحسابات الخاملة.
          استخدم المتغيرات التالية داخل النص وسيتم استبدالها تلقائياً بقيم كل قارئ:
          <div className="mt-2 flex flex-wrap gap-1.5">
            {variables.map(v => (
              <code key={v} className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-mono text-[11px]">{`{${v}}`}</code>
            ))}
          </div>
          <p className="mt-1 text-[11px] text-emerald-800/80">
            • <b>name</b> = اسم القارئ · <b>title</b> = اللقب (للقارئات) · <b>inactive_days</b> = عدد أيام الخمول
            · <b>missed_quota</b> = الفوائت (بالإشارة السالبة) · <b>missed_abs</b> = القيمة المطلقة للفوائت
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-medium text-accent">قالب رسالة القراء (ذكور)</label>
            {isMaster && (
              <button
                onClick={() => resetTo('male')}
                className="text-[11px] text-muted-foreground hover:text-accent flex items-center gap-1"
                data-testid="reset-male-btn"
              >
                <RotateCcw className="w-3 h-3" /> النص الافتراضي
              </button>
            )}
          </div>
          <textarea
            value={male}
            onChange={(e) => setMale(e.target.value)}
            disabled={!isMaster}
            rows={9}
            dir="rtl"
            className="w-full px-4 py-3 border rounded-xl text-sm leading-relaxed focus:ring-2 focus:ring-emerald-300/40 focus:border-emerald-500 resize-y disabled:bg-gray-50 disabled:cursor-not-allowed"
            data-testid="whatsapp-male-textarea"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-medium text-accent">قالب رسالة القارئات (إناث)</label>
            {isMaster && (
              <button
                onClick={() => resetTo('female')}
                className="text-[11px] text-muted-foreground hover:text-accent flex items-center gap-1"
                data-testid="reset-female-btn"
              >
                <RotateCcw className="w-3 h-3" /> النص الافتراضي
              </button>
            )}
          </div>
          <textarea
            value={female}
            onChange={(e) => setFemale(e.target.value)}
            disabled={!isMaster}
            rows={9}
            dir="rtl"
            className="w-full px-4 py-3 border rounded-xl text-sm leading-relaxed focus:ring-2 focus:ring-emerald-300/40 focus:border-emerald-500 resize-y disabled:bg-gray-50 disabled:cursor-not-allowed"
            data-testid="whatsapp-female-textarea"
          />
        </div>
      </div>

      {!isMaster && (
        <p className="text-xs text-muted-foreground">تحرير القوالب متاح للمدير المطلق فقط.</p>
      )}
    </Card>
  );
};

export default WhatsAppTemplatesSection;
